// react
import type { SyntheticEvent } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useLocation, useNavigate } from '@tanstack/react-router';

// material ui
import { Tab, TabProps, Tabs, TabsProps } from '@mui/material';
import { styled } from '@mui/material/styles';

// components
import useSearchParams from '@/hooks/useStableSearchParams';

const StyledTabs = styled((props: TabsProps) => (
  <Tabs
    {...props}
    slotProps={{
      indicator: { children: <span className="MuiTabs-indicatorSpan" /> }
    }}
  />
))(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    bottom: 1,
  },
  '& .MuiTabs-indicatorSpan': {
    height: 4,
    width: '100%',
    borderRadius: 5,
    backgroundColor: (
      theme.palette.remoni as unknown as Record<number, string>
    )[800],
  },
}));

const StyledTab = styled((props: TabProps) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: 'none',
    minWidth: 0,
    minHeight: 48,
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(15),
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.primary.main,
      opacity: 1,
    },
    '&.Mui-selected': {
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightMedium,
    },
    '&.Mui-focusVisible': {
      backgroundColor: theme.palette.action.focus,
    },
  }),
);

const PortfolioAnalysisDashboardPresetTabs = () => {
  const { t } = useTranslation('portfolio-analysis');
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const presetKeys = ['standard', 'building'];

  const activePreset = presetKeys.findIndex((preset) =>
    location.pathname.includes(preset),
  );

  const presetItems = presetKeys.map((preset, index) => ({
    label: t(`presets.${preset}`),
    path: preset,
    key: preset,
    index,
  }));

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    const selectedPreset = presetItems[newValue];
    navigate({
      to: `../${selectedPreset.path}?${searchParams.toString()}`,
    });
  };

  return (
    <StyledTabs value={activePreset} onChange={handleTabChange}>
      {presetItems.map((preset) => (
        <StyledTab key={preset.key} label={preset.label} />
      ))}
    </StyledTabs>
  );
};

export default PortfolioAnalysisDashboardPresetTabs;
